import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Box, Grid, Skeleton, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { getEntry, getEntries } from '../services/contentfulClient';
import Flag from 'react-flagkit';
import Seo from '../components/seo/Seo';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const contentfulLocale = (i18nLang) => {
  const normalizedLang = (i18nLang || '').split('-')[0];
  const i18nToContentfulLocale = { en: 'en-US', pl: 'pl-PL' };
  return i18nToContentfulLocale[normalizedLang] || normalizedLang;
};

const normalizeAssetUrl = (url) => {
  if (!url) return '';
  return url.startsWith('//') ? `https:${url}` : url;
};

const assetUrl = (asset) =>
  normalizeAssetUrl(asset?.fields?.file?.url);

async function fetchProject(param, locale) {
  const opts = { locale, include: 2 };
  try {
    return await getEntry(param, opts);
  } catch (e) {
    const status = e?.response?.status;
    if (status && status !== 404) {
      throw e;
    }
    const { items } = await getEntries({
      content_type: 'project',
      'fields.slug': param,
      limit: 1,
      ...opts,
    });
    return items[0] ?? null;
  }
}
  
const ProjectDetail = () => {
  const navigate = useNavigate();
  const { id: slugOrId } = useParams();
  const { t, i18n } = useTranslation('projects');
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setEntry(null);
      try {
        const locale = contentfulLocale(i18n.language);
        const result = await fetchProject(slugOrId, locale);
        if (!cancelled) setEntry(result);
      } catch (err) {
        console.warn('Contentful project:', err.message);
        if (!cancelled) setEntry(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [slugOrId, i18n.language]);

  if (loading) {
    return (
      <Container>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Skeleton width="85%" height={40} />
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
              <Skeleton variant="circular" width={28} height={28} />
              <Skeleton width={120} height={24} />
            </Box>

            <Skeleton width="100%" height={18} sx={{ mb: 1 }} />
            <Skeleton width="95%" height={18} sx={{ mb: 1 }} />
            <Skeleton width="80%" height={18} sx={{ mb: 2 }} />

            <Skeleton variant="rectangular" width={200} height={130} sx={{ mb: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Skeleton width={120} height={18} />
              <Skeleton width={70} height={18} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Skeleton width={120} height={18} />
              <Skeleton width={70} height={18} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Skeleton width={120} height={18} />
              <Skeleton width={70} height={18} />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={8}>
            <Skeleton variant="rectangular" width="100%" height={360} sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              {Array.from({ length: 6 }).map((_, idx) => (
                <Grid item xs={6} sm={4} md={2} key={`photo-skeleton-${idx}`}>
                  <Skeleton variant="rectangular" width="100%" height={120} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!entry) {
    return (
      <Container>
        <Typography variant="h6">{t('notFound')}</Typography>
      </Container>
    );
  }

  const f = entry.fields || {};
  const name = f.name || '';
  const cover = assetUrl(f.coverPhoto);
  const mapImage = assetUrl(f.map);
  const photos = Array.isArray(f.photos) ? f.photos : [];
  const countryCode = f.couuntryCode || f.countryCode;
  const title = name ? `${name} - Betonoki` : `Betonoki - ${t('project', { defaultValue: 'Project' })}`;
  const description =
    (typeof f?.shortDescription === 'string' && f.shortDescription.trim()) ||
    (typeof f?.summary === 'string' && f.summary.trim()) ||
    t('projectMetaDescription', {
      defaultValue: 'Szczegóły projektu Betonoki: opis, lokalizacja i galeria.',
    });

  return (
    <>
      <Seo
        title={title}
        description={description}
        path={`/project/${slugOrId}`}
        image={cover || '/img/logo.png'}
      />
      <IconButton
        color="primary"
        onClick={() => navigate('/projects')}
        sx={{
          position: 'fixed',
          top: '3rem',
          left: { xs: '3rem', md: 'calc(200px + 3rem)' },
          zIndex: 10,
        }}
      >
        <ArrowBackIcon />
      </IconButton>
      <Container>

        <Grid container spacing={2} sx={{ mb: '2rem' }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="h1" sx={{ mb: 2 }}>
              {name}
            </Typography>
            {(f.investorLogo || f.investorName) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <img style={{maxWidth: "100px", maxHeight: "100px", display: "block"}} src={assetUrl(f.investorlogo)} alt={f.investorName} />
              <Typography>{f.investorName}</Typography>
            </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                flexWrap: 'wrap',
                mb: 1,
                justifyContent: 'space-between',
              }}
            >
                {f.projectType ? (
                <Typography variant="body1" component="span">
                  {f.projectType}
                </Typography>
              ) : null}

              {countryCode ? (
                <Flag
                  country={String(countryCode).toUpperCase()}
                  size={28}
                />
              ) : null}
            
            </Box>

            {f.description ? (
          <Box sx={{ mb: 3, '& p': { mb: 1 } }}>
            {documentToReactComponents(f.description)}
          </Box>
        ) : null}

        {f.map ? (
          <Box sx={{ mb: 3 }}>
            <img
              src={mapImage}
              alt=""
              style={{ width: '100%', maxWidth: "200px", height: 'auto', display: 'block' }}
            />
          </Box>
        ) : null}

{f.location && (
<Box display="flex" alignItems="center" gap={1} justifyContent="space-between">

<Typography variant="subtitle2" color="text.secondary">
                {t('location')}
              </Typography>
              <Typography variant="body1">{f.location}</Typography>
</Box>
)}
{f.projectArea && (   
<Box display="flex" alignItems="center" gap={1} justifyContent="space-between">

<Typography variant="subtitle2" color="text.secondary">
                {t('projectArea')}
              </Typography>
              {/* <Typography variant="body1">{f.projectArea} m<sup>2</sup></Typography> */}
</Box>
)}
{f.builtUpArea && (
<Box display="flex" alignItems="center" gap={1} justifyContent="space-between">

<Typography variant="subtitle2" color="text.secondary">
                {t('builtUpArea')}
              </Typography>
              <Typography variant="body1">{f.builtUpArea} m<sup>2</sup></Typography>
</Box>
)}
        
          </Grid>


          <Grid item xs={12} sm={6} md={8}>
          {cover ? (
          <Box sx={{ mb: 3 }}>
            <img
              src={cover}
              alt=""
              style={{ width: '100%', maxWidth: 900, height: 'auto', display: 'block' }}
            />
          </Box>
        ) : null}
          </Grid>
        </Grid>
        



        
        {photos.length > 0 ? (
          <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
            {photos.map((asset) => {
              const url = assetUrl(asset);
              if (!url) return null;
              return (
                
                  <img
                    src={url}
                    alt=""
                    style={{maxWidth: '800px', maxHeight: '400px', display: 'block' }}
                  />
              );
            })}
          </Box>
        ) : null}
      </Container>
    </>
  );
};

export default ProjectDetail;


